// src/components/instructor/ops/RequestsView.js
//
// Pass 50A (items 5–8) — the Requests view: the operational-sales starting point. A dense,
// scannable table of booking CASES (customer enquiries) feeding the schedule, plus a case
// detail workspace. Read model derived from bookingRequests → normalizeBookingCase; child
// assignment progress comes from the canonical bookings. This is the PARENT-case view — not a
// second scheduling system.

import React, { useMemo, useState, useEffect } from 'react';
import {
  normalizeBookingCase, buildCaseRow, caseAssignmentProgress, casePaymentState, CASE_STAGE_LABEL, CASE_STAGE,
  deriveCaseOperationalFlow, caseSlotRows,
} from '../../../utils/bookingCase';
import { paymentLabel } from '../../../utils/paymentSemantics';
import IntakeInbox from './IntakeInbox';
import AssignControl from './AssignControl';

const V = { t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95', border: '#E2E0DB', s2: '#F2F1EE', green: '#1D9E75', amber: '#C97D10', red: '#DC2626' };
const F = "'DM Sans',sans-serif";
const stageColor = { inquiry: '#1A3FA0', confirmed: '#085041', completed: '#57534E', cancelled: '#991B1B' };
const payColor = { paid: '#085041', deposit_paid: '#1A3FA0', not_paid: '#92400E', inquiry: '#92400E', not_recorded: '#9E9B95' };
const linkBtn = { border: 'none', background: 'none', color: '#1A3FA0', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0, textDecoration: 'underline' };
// Pass 65 — compact derived operational progress for the Requests list (triage without opening each case).
function compactFlow(flow) {
  if (!flow || flow.synthesized || flow.expectedSlots === 0) return null;
  const style = { display: 'block', fontSize: 10.5, color: '#6B6860', marginTop: 3 };
  if (flow.nextAction.kind === 'scheduled') return <span style={{ ...style, color: '#1D9E75', fontWeight: 700 }}>Scheduled · {flow.assignedSlots}/{flow.expectedSlots}</span>;
  if (flow.stage === 'confirmed') return <span style={style}>{flow.createdSlots}/{flow.expectedSlots} bookings{flow.unassignedSlots ? ` · ${flow.unassignedSlots} unassigned` : ''}</span>;
  return null;
}
// Pass 65 — slot state as a TEXT label (never colour-only), so Unassigned/Assigned/Not created are legible.
function slotStateChip(state) {
  const map = { assigned: ['#085041', '#E1F5EE', 'Assigned'], unassigned: ['#92400E', '#FEF3C7', 'Unassigned'], not_created: ['#6B6860', '#F2F1EE', 'Not created'] };
  const [fg, bg, label] = map[state] || map.not_created;
  return <span style={{ padding: '1px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: bg, color: fg }}>{label}</span>;
}

export default function RequestsView({ cases = [], bookings = [], seasonId, readOnly = false, onNewBooking, caseActions = null, intakes = [], intakeActions = null, organizationId = '', seasonMembers = [], onOpenBooking = null, onOpenSchedule = null, openCaseId = '', onCaseOpened = null, organization = null }) {
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('enquiries');
  // Pass 65 — a booking's "Open enquiry" traceback selects its originating case here.
  useEffect(() => {
    if (openCaseId) { setTab('enquiries'); setSelectedId(openCaseId); onCaseOpened && onCaseOpened(); }
  }, [openCaseId]); // eslint-disable-line react-hooks/exhaustive-deps
  // §P synthesized cases are already normalized and carry read-only metadata — don't re-normalize
  // (that would strip `synthesized`/`_synthLines`); only normalize raw Firestore cases.
  // (Declared before any early return so hook order is stable across the enquiries/intake tabs.)
  const normalized = useMemo(() => cases.map((c) => (c.synthesized ? c : normalizeBookingCase(c))), [cases]);
  const rows = useMemo(() => normalized.map((c) => ({ ...buildCaseRow(c, bookings), synthesized: !!c.synthesized, flow: deriveCaseOperationalFlow(c, bookings) })), [normalized, bookings]);

  // Pass 64 — one surface, two axes: booking enquiries (commercial cases) and source intake (the front
  // door). Intake items become enquiries via operator review; they are NOT a second booking model.
  const openIntake = intakes.filter((i) => i.reviewState === 'new' || i.reviewState === 'needs_review').length;
  const Tabs = () => (
    <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: `1px solid ${V.border}` }}>
      {[['enquiries', 'Booking enquiries'], ['intake', `Source intake${openIntake ? ` (${openIntake})` : ''}`]].map(([k, lbl]) => (
        <button key={k} data-testid={`requests-tab-${k}`} onClick={() => { setTab(k); setSelectedId(null); }} style={{ padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: tab === k ? V.t1 : V.t3, borderBottom: `2px solid ${tab === k ? V.t1 : 'transparent'}`, marginBottom: -1 }}>{lbl}</button>
      ))}
    </div>
  );
  if (tab === 'intake' && !selectedId) {
    return <div style={{ fontFamily: F }}><Tabs /><IntakeInbox intakes={intakes} organizationId={organizationId} seasonId={seasonId} readOnly={readOnly} actions={intakeActions} /></div>;
  }

  const cell = { padding: '8px 10px', borderBottom: `1px solid ${V.border}`, fontSize: 12.5, textAlign: 'left', verticalAlign: 'top' };
  const chip = (bg, fg, text) => <span style={{ padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: bg, color: fg }}>{text}</span>;

  if (selectedId) {
    const c = normalized.find((x) => x.id === selectedId);
    if (!c) { setSelectedId(null); return null; }
    return <CaseDetail caseObj={c} bookings={bookings} seasonId={seasonId} seasonMembers={seasonMembers} onBack={() => setSelectedId(null)} readOnly={readOnly} caseActions={caseActions} onOpenBooking={onOpenBooking} onOpenSchedule={onOpenSchedule} organization={organization} />;
  }

  // Pass 61 — the Requests tab IS the booking-enquiries (booking CASE) operator surface. One row = one
  // customer case (an enquiry that becomes one-or-many bookings), NOT an arbitrary technical record.
  const Heading = () => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: V.t1 }}>Booking enquiries</h2>
      {rows.length ? <span style={{ fontSize: 12.5, color: V.t2 }}>{rows.length} {rows.length === 1 ? 'case' : 'cases'} this season</span> : null}
    </div>
  );

  if (!rows.length) {
    return (
      <div style={{ fontFamily: F }}>
        <Tabs />
        <Heading />
        <div data-testid="requests-empty" style={{ padding: 28, textAlign: 'center', color: V.t3, fontSize: 13, border: `1px dashed ${V.border}`, borderRadius: 12 }}>
          No booking enquiries for this season yet. New enquiries appear here as one case per customer.
          {!readOnly && onNewBooking ? <div style={{ marginTop: 10 }}><button onClick={onNewBooking} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: V.t1, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ New booking</button></div> : null}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="requests-view" style={{ fontFamily: F }}>
      <Tabs />
      <Heading />
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <thead>
          <tr style={{ color: V.t3, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <th style={cell}>Customer</th><th style={cell}>Dates</th><th style={cell}>Days</th><th style={cell}>Instructor-days</th>
            <th style={cell}>Assigned</th><th style={cell}>Stage</th><th style={cell}>Payment</th><th style={cell}>Invoice</th><th style={cell}>Source</th><th style={cell} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`request-row-${r.id}`} onClick={() => setSelectedId(r.id)} style={{ cursor: 'pointer', background: r.attention ? '#FFFDF5' : 'transparent' }}>
              <td style={{ ...cell, fontWeight: 600 }}>{r.name}{r.attention ? <span title="Needs attention" style={{ color: V.amber, marginLeft: 5 }}>●</span> : null}{r.synthesized ? <span title="Synthesized from an imported source booking — read-only" style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: V.t3, background: V.s2, borderRadius: 4, padding: '1px 5px' }}>SOURCE</span> : null}</td>
              <td style={cell}>{r.dateStart}{r.dateEnd && r.dateEnd !== r.dateStart ? ` – ${r.dateEnd}` : ''}</td>
              <td style={cell}>{r.days}</td>
              <td style={cell}>{r.instructorDays}</td>
              <td style={cell}>{chip(r.assigned >= r.required && r.required > 0 ? '#E1F5EE' : '#FEF3C7', r.assigned >= r.required && r.required > 0 ? V.green : V.amber, r.assignmentLabel)}</td>
              <td style={cell}>{chip('#EFF1F6', stageColor[r.stage] || V.t2, CASE_STAGE_LABEL[r.stage] || r.stage)}{compactFlow(r.flow)}</td>
              <td style={cell}>{chip('#F4F3F0', payColor[r.paymentState] || V.t2, paymentLabel[r.paymentState] || r.paymentState)}</td>
              <td style={cell}>{r.invoiceNumber || '—'}</td>
              <td style={cell}>{r.source}</td>
              <td style={cell}>›</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// Pass 62 — the operator "requested sessions → canonical bookings" conversion control. Explicit, idempotent,
// actor-authorized; a synthesized/source case is read-only. A confirmed case's children flow through the
// SAME canonical scheduling pipeline (Unassigned/Calendar/Day/Week/Master Schedule) — no parallel object.
function BookingGeneration({ caseObj, bookings, prog, readOnly, caseActions }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hh = { fontSize: 11, fontWeight: 700, color: V.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const children = bookings.filter((b) => b.bookingRequestId === caseObj.id && b.status !== 'canceled');
  const required = prog.required;                 // effective instructor slots (respects cancelled slots/lines)
  const created = children.length;
  const missing = Math.max(0, required - created);
  const isReadOnly = readOnly || !!caseObj.synthesized || !caseActions;
  const run = async (fn) => { if (busy || !fn) return; setBusy(true); setError(''); try { await fn(caseObj); } catch (e) { setError(e?.message || 'Could not complete this action.'); } finally { setBusy(false); } };

  let action = null;
  if (!isReadOnly) {
    if (caseObj.stage === CASE_STAGE.INQUIRY) action = { label: busy ? 'Creating…' : `Confirm & create ${required} booking${required === 1 ? '' : 's'}`, fn: caseActions.confirmAndCreate };
    else if (caseObj.stage === CASE_STAGE.CONFIRMED && missing > 0) action = { label: busy ? 'Creating…' : `Create ${missing} missing booking${missing === 1 ? '' : 's'}`, fn: caseActions.createMissing };
  }
  return (
    <div style={{ marginBottom: 16, background: '#F7F6F3', border: `1px solid ${V.border}`, borderRadius: 10, padding: '12px 14px' }} data-testid="booking-generation">
      <div style={hh}>Booking generation</div>
      <div style={{ fontSize: 12.5, color: V.t2, marginBottom: action ? 9 : 0 }}>
        {prog.lines.length} requested session{prog.lines.length === 1 ? '' : 's'} · {required} instructor slot{required === 1 ? '' : 's'} · <strong style={{ color: V.t1 }}>{created} booking{created === 1 ? '' : 's'} created</strong>
        {missing > 0 ? <> · <span style={{ color: V.amber, fontWeight: 700 }}>{missing} still to create</span></> : (required > 0 && created > 0 ? <> · <span style={{ color: V.green, fontWeight: 700 }}>all created</span></> : null)}
        {caseObj.synthesized ? <span style={{ marginLeft: 6, color: V.t3 }}>· source-derived, read-only</span> : null}
      </div>
      {action && <button disabled={busy} onClick={() => run(action.fn)} data-testid="case-generate-bookings" style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: busy ? '#9DB4F0' : V.t1, color: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>{action.label}</button>}
      {!action && caseObj.stage === CASE_STAGE.CONFIRMED && required > 0 && missing === 0 && <div style={{ fontSize: 12.5, color: V.green, fontWeight: 700 }}>Bookings created · {created}/{required}</div>}
      {action && caseObj.stage === CASE_STAGE.INQUIRY && <div style={{ fontSize: 11.5, color: V.t3, marginTop: 6 }}>Confirming turns this enquiry into operational schedule demand — new bookings start Unassigned.</div>}
      {error && <div role="alert" style={{ color: V.red, fontSize: 12.5, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function CaseDetail({ caseObj, bookings, seasonId, seasonMembers = [], onBack, readOnly, caseActions, onOpenBooking, onOpenSchedule, organization = null }) {
  const prog = caseAssignmentProgress(caseObj, bookings);
  const flow = deriveCaseOperationalFlow(caseObj, bookings);
  const slots = caseSlotRows(caseObj, bookings);
  const sec = { marginBottom: 16 };
  const h = { fontSize: 11, fontWeight: 700, color: V.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const cell = { padding: '6px 10px', borderBottom: `1px solid ${V.border}`, fontSize: 12.5, textAlign: 'left' };
  return (
    <div data-testid="case-detail" style={{ fontFamily: F, color: V.t1 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: V.t2, cursor: 'pointer', fontSize: 13, marginBottom: 8 }}>‹ All requests</button>
      <h2 style={{ margin: '0 0 2px', fontSize: 20 }}>{caseObj.requesterName || caseObj.leadGuest?.fullName || 'New enquiry'}</h2>
      <div style={{ fontSize: 12.5, color: V.t2, marginBottom: 14 }}>
        {caseObj.requesterEmail || '—'}{caseObj.requesterPhone ? ` · ${caseObj.requesterPhone}` : ''}
      </div>

      {/* B. Status + payment (separate axes) */}
      <div style={{ ...sec, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div><div style={h}>Stage</div>{CASE_STAGE_LABEL[caseObj.stage] || caseObj.stage}</div>
        <div><div style={h}>Payment</div>{paymentLabel[casePaymentState(caseObj)]}</div>
        <div><div style={h}>Assignment</div>{prog.assigned} / {prog.required}{prog.complete ? ' ✓' : ''}</div>
        <div><div style={h}>Cancellation</div>{caseObj.cancellationResolution || 'None'}</div>
        <div><div style={h}>Invoice</div>{caseObj.invoiceNumber || '—'}</div>
        <div><div style={h}>Source</div>{caseObj.sourceType}{caseObj.roombossNumber ? ` · RoomBoss ${caseObj.roombossNumber}` : ''}</div>
      </div>

      {/* Pass 62 — booking generation: confirm an enquiry and create exactly the missing canonical bookings. */}
      <BookingGeneration caseObj={caseObj} bookings={bookings} prog={prog} readOnly={readOnly} caseActions={caseActions} />

      {/* Pass 65 — Bookings & scheduling: the case → booking → schedule operator journey. Per-slot child
          bookings are first-class rows; assignment reuses the canonical AssignControl (same eligibility /
          writer / conflict rules as operations); navigation deep-links into the schedule. */}
      {!caseObj.synthesized && flow.expectedSlots > 0 && (
        <div style={sec} data-testid="bookings-scheduling">
          <div style={{ ...h, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            Bookings &amp; scheduling
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: V.t2 }}>
              {flow.createdSlots}/{flow.expectedSlots} created · {flow.assignedSlots}/{flow.expectedSlots} assigned
            </span>
            {flow.nextAction.kind === 'scheduled' && <span data-testid="flow-scheduled" style={{ textTransform: 'none', letterSpacing: 0, color: V.green, fontWeight: 700 }}>· Scheduled</span>}
            {flow.nextAction.kind === 'assign' && <span data-testid="flow-nextaction" style={{ textTransform: 'none', letterSpacing: 0, color: V.amber, fontWeight: 700 }}>· Next: {flow.nextAction.label}</span>}
          </div>
          {slots.some((s) => s.state !== 'not_created') ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ color: V.t3, fontSize: 11, textTransform: 'uppercase' }}><th style={cell}>Date</th><th style={cell}>Activity</th><th style={cell}>Slot</th><th style={cell}>State</th><th style={cell}>Instructor / action</th></tr></thead>
              <tbody>
                {slots.map((s) => (
                  <tr key={s.requestSlotId} data-testid={`slot-${s.requestSlotId}`}>
                    <td style={cell}>{s.date || '—'}{s.startTime ? ` ${s.startTime}` : ''}</td>
                    <td style={cell}>{s.bookingType || '—'}{s.discipline ? ` · ${s.discipline}` : ''}</td>
                    <td style={cell}>{s.multi ? `#${s.slot}` : '—'}</td>
                    <td style={cell}>{slotStateChip(s.state)}</td>
                    <td style={cell}>
                      {s.state === 'assigned' ? (
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600 }}>{s.instructorName || s.instructorId}</span>
                          {onOpenSchedule && s.date ? <button data-testid={`open-schedule-${s.requestSlotId}`} onClick={() => onOpenSchedule({ view: 'day', date: s.date, bookingId: s.bookingId })} style={linkBtn}>Open in schedule</button> : null}
                          {onOpenBooking && s.booking ? <button onClick={() => onOpenBooking(s.booking)} style={linkBtn}>Open booking</button> : null}
                        </span>
                      ) : s.state === 'unassigned' ? (
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!readOnly && s.booking ? <AssignControl booking={s.booking} seasonMembers={seasonMembers} seasonId={seasonId} organization={organization} readOnly={readOnly} /> : null}
                          {onOpenSchedule ? <button data-testid={`open-unassigned-${s.requestSlotId}`} onClick={() => onOpenSchedule({ view: 'unassigned', date: s.date, bookingId: s.bookingId })} style={linkBtn}>Open in Unassigned</button> : null}
                        </span>
                      ) : (
                        <span style={{ color: V.t3 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12.5, color: V.t2 }}>No bookings created yet. {caseObj.stage === CASE_STAGE.INQUIRY ? 'Confirm the enquiry above to create them.' : 'Use “Create missing bookings” above.'}</div>
          )}
        </div>
      )}

      {/* C. Requested sessions + D. assignment */}
      <div style={sec}>
        <div style={h}>Requested sessions</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ color: V.t3, fontSize: 11, textTransform: 'uppercase' }}><th style={cell}>Date</th><th style={cell}>Activity</th><th style={cell}>Discipline</th><th style={cell}>Skill</th><th style={cell}>Location</th><th style={cell}>Assigned</th><th style={cell}>Status</th></tr></thead>
          <tbody>
            {prog.lines.map((l) => (
              <tr key={l.lineId}>
                <td style={cell}>{l.date || '—'}{l.startTime ? ` ${l.startTime}` : ''}</td>
                <td style={cell}>{l.bookingType || l.activityType}</td>
                <td style={cell}>{l.discipline || '—'}</td>
                <td style={cell}>{l.skillLevel || '—'}</td>
                <td style={cell}>{l.pickup || l.location || '—'}</td>
                <td style={cell}>{l.assigned} / {l.required}</td>
                <td style={cell}>{l.status}</td>
              </tr>
            ))}
            {!prog.lines.length && <tr><td style={cell} colSpan={7}>No requested sessions recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* E. Accommodation / pickup */}
      {(caseObj.accommodationName || caseObj.accommodationAddress) && (
        <div style={sec}><div style={h}>Accommodation / pickup</div>{caseObj.accommodationName}{caseObj.accommodationAddress ? ` — ${caseObj.accommodationAddress}` : ''}{caseObj.accommodationMapUrl ? <> · <a href={caseObj.accommodationMapUrl} target="_blank" rel="noreferrer">map</a></> : null}</div>
      )}

      {/* G. Communication / source history (email provenance timeline) */}
      <div style={sec}>
        <div style={h}>Communication / source history</div>
        {caseObj.communications.length ? caseObj.communications.map((c) => (
          <div key={c.id} data-testid="comm-entry" style={{ padding: '6px 0', borderBottom: `1px solid ${V.border}`, fontSize: 12.5 }}>
            <span style={{ fontWeight: 600 }}>{c.channel}</span>{c.direction ? ` · ${c.direction}` : ''}{c.subject ? ` · ${c.subject}` : ''}
            <span style={{ color: V.t3 }}>{c.messageId ? ` · msg ${c.messageId}` : ''}{c.threadId ? ` · thread ${c.threadId}` : ''}</span>
            {c.snippet ? <div style={{ color: V.t2 }}>{c.snippet}</div> : null}
          </div>
        )) : <div style={{ color: V.t3, fontSize: 12.5 }}>No communications recorded. {readOnly ? '' : 'Add a note or link an email as source provenance.'}</div>}
      </div>

      {readOnly && <div style={{ fontSize: 12, color: V.amber }}>Historical / read-only — case editing disabled.</div>}
    </div>
  );
}
